/**
 * Admin API Service
 *
 * Modular API client for the Kynetik admin panel.
 * Split into domain-specific modules for better maintainability.
 */

// Export the base client for session management
export { apiClient } from './client';

// Export all types
export * from './types';

// Export domain-specific APIs
export { auditLogsApi } from './audit-logs';
export { couponsApi, customersApi, giftCardsApi } from './customers';
export { dashboardApi } from './dashboard';
export { devicesApi } from './devices';
export { exchangesApi } from './exchanges';
export { inventoryApi } from './inventory';
export { locationsApi } from './locations';
export { paymentProvidersApi, paymentReadersApi } from './payments';
export { pricelistsApi } from './pricelists';
export { productsApi } from './products';
export { promotionsApi } from './promotions';
export { reportsApi } from './reports';
export { settingsApi } from './settings';
export { staffApi } from './staff';
export { storeApi } from './store';
export { subscriptionsApi } from './subscriptions';

// Re-export ApiError for error handling
export { ApiError } from '@/utils/errors';

// ===========================================
// Unified API facade for backwards compatibility
// ===========================================

import { auditLogsApi } from './audit-logs';
import { apiClient } from './client';
import { couponsApi, customersApi, giftCardsApi } from './customers';
import { dashboardApi } from './dashboard';
import { devicesApi } from './devices';
import { exchangesApi } from './exchanges';
import { inventoryApi } from './inventory';
import { locationsApi } from './locations';
import { paymentProvidersApi, paymentReadersApi } from './payments';
import { pricelistsApi } from './pricelists';
import { productsApi } from './products';
import { promotionsApi } from './promotions';
import { reportsApi } from './reports';
import { settingsApi } from './settings';
import { staffApi } from './staff';
import { storeApi } from './store';
import { subscriptionsApi } from './subscriptions';

/**
 * Unified API object that mirrors the old ApiClient class interface.
 * This provides backwards compatibility for existing code.
 */
export const api = {
  // Session management
  setSession: (sessionToken: string, storeHash: string) => {
    apiClient.setSession(sessionToken, storeHash);
  },
  revokeSession: (sessionToken: string) =>
    apiClient.request(`/admin/sessions/${encodeURIComponent(sessionToken)}`, { method: 'DELETE' }),

  // Dashboard
  getDashboardStats: (params?: { channelId?: string }) => dashboardApi.getStats(params),
  getRecentOrders: (params?: { channelId?: string }) => dashboardApi.getRecentOrders(params),
  runMigrations: () => dashboardApi.runMigrations(),

  // Devices
  getDevices: devicesApi.list,
  createDevice: devicesApi.create,
  updateDevice: devicesApi.update,
  deleteDevice: devicesApi.delete,
  regenerateDeviceKey: devicesApi.regenerateKey,
  reconnectDevice: devicesApi.reconnect,
  createDevicePairingCode: devicesApi.createPairingCode,
  regenerateDevicePairingCode: devicesApi.regeneratePairingCode,
  getDeviceInfo: devicesApi.getInfo,

  // Staff
  getPOSStaff: staffApi.list,
  addPOSStaff: staffApi.create,
  updatePOSStaff: staffApi.update,
  deletePOSStaff: staffApi.delete,
  resetStaffPIN: staffApi.resetPIN,
  setStaffPIN: staffApi.setPIN,
  updateStaffPermissions: staffApi.updatePermissions,
  getBCUsers: () => staffApi.listBCUsers(),
  syncBCUsers: () => staffApi.syncBCUsers(),

  // Locations
  getLocations: () => locationsApi.list(),
  enableLocation: locationsApi.enable,
  updateLocation: locationsApi.update,
  disableLocation: locationsApi.disable,
  assignDeviceToLocation: locationsApi.assignDevice,

  // Settings
  getSettings: () => settingsApi.get(),
  updateSettings: settingsApi.update,
  getCurrencies: () => settingsApi.getCurrencies(),
  getTaxZones: () => settingsApi.getTaxZones(),
  getTaxRates: settingsApi.getTaxRates,
  getTaxClasses: () => settingsApi.getTaxClasses(),
  getTaxSync: () => settingsApi.getTaxSync(),
  getOrderStatuses: () => settingsApi.getOrderStatuses(),
  getPickupMethods: () => settingsApi.getPickupMethods(),
  createPickupMethod: settingsApi.createPickupMethod,
  autoSetupPickupMethods: () => settingsApi.autoSetupPickupMethods(),
  getChannels: () => settingsApi.getChannels(),
  updateChannel: settingsApi.updateChannel,

  // Store
  getStoreInfo: () => storeApi.getInfo(),
  getCategories: () => storeApi.getCategories(),
  getBrands: () => storeApi.getBrands(),
  getCustomerGroups: () => storeApi.getCustomerGroups(),

  // Customers
  searchCustomers: customersApi.search,
  getCustomerDetails: customersApi.getDetails,
  getCustomerAttributes: () => customersApi.getAttributes(),
  searchCustomersByAttribute: customersApi.searchByAttribute,
  getCustomerStoreCredit: customersApi.getStoreCredit,
  applyStoreCredit: customersApi.applyStoreCredit,

  // Coupons
  getCoupons: couponsApi.list,
  validateCoupon: couponsApi.validate,

  // Gift Cards
  checkGiftCardBalance: giftCardsApi.checkBalance,
  redeemGiftCard: giftCardsApi.redeem,
  createGiftCard: giftCardsApi.create,

  // Promotions
  getPromotions: promotionsApi.list,
  getPromotion: promotionsApi.get,

  // Price Lists
  getPriceLists: () => pricelistsApi.list(),

  // Payment Providers
  getPaymentProviders: () => paymentProvidersApi.list(),
  getPaymentProvider: paymentProvidersApi.get,
  configurePaymentProvider: paymentProvidersApi.configure,
  updatePaymentProvider: paymentProvidersApi.update,
  disablePaymentProvider: paymentProvidersApi.disable,

  // Payment Readers
  getPaymentReaders: paymentReadersApi.list,
  registerPaymentReader: paymentReadersApi.register,
  updatePaymentReader: paymentReadersApi.update,
  unregisterPaymentReader: paymentReadersApi.unregister,

  // Reports
  getSalesSummary: reportsApi.getSalesSummary,
  getSalesByStaff: reportsApi.getSalesByStaff,
  getSalesByHour: reportsApi.getSalesByHour,
  getSalesByPaymentMethod: reportsApi.getSalesByPaymentMethod,
  exportReport: reportsApi.exportReport,

  // Products
  getBarcodeFieldOptions: () => productsApi.getBarcodeFieldOptions(),

  // Exchanges
  getExchanges: exchangesApi.list,
  getExchange: exchangesApi.get,

  // Audit Logs (Business tier)
  getAuditLogs: auditLogsApi.list,
  getAuditLogActions: auditLogsApi.getActions,
  getAuditLogStats: auditLogsApi.getStats,
  exportAuditLogs: auditLogsApi.export,

  // Subscriptions (Billing)
  getSubscription: () => subscriptionsApi.get(),
  getSubscriptionEvents: () => subscriptionsApi.getEvents(),
  getSubscriptionLimits: () => subscriptionsApi.getLimits(),
  previewPlanChange: subscriptionsApi.previewPlanChange,
  previewAddonChange: subscriptionsApi.previewAddonChange,
  previewBillingCycleChange: subscriptionsApi.previewBillingCycleChange,
  changePlan: subscriptionsApi.changePlan,
  changeBillingCycle: subscriptionsApi.changeBillingCycle,
  addSubscriptionAddon: subscriptionsApi.addAddon,
  removeSubscriptionAddon: subscriptionsApi.removeAddon,
  cancelSubscription: () => subscriptionsApi.cancel(),
  reactivateSubscription: () => subscriptionsApi.reactivate(),

  // Inventory
  getInventory: inventoryApi.getInventory,
  adjustInventory: inventoryApi.adjustInventory,
  getAdjustmentHistory: inventoryApi.getAdjustmentHistory,
  searchInventoryProducts: inventoryApi.searchProducts,
};
